/**
 * Mushroom stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const mushroomStickersPack: ToilePack = {
    slug: 'mushroom-stickers',
    category: 'sticker',
    name: 'Mushroom stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'agaric', label: 'Fly agaric', subject: 'a red-capped mushroom with white spots and a thick stem'},
        {key: 'cluster', label: 'Cluster', subject: 'a cluster of small brown mushrooms with moss at the base'},
        {key: 'face', label: 'Mushroom face', subject: 'a mushroom with a small smiling face and a blush'},
    ],
    styles: [
        {key: 'retro', label: 'Retro', style: 'retro sticker illustration, 1970s shapes, halftone grain, slight misregistration', palette: 'Mustard, rust, teal and cream', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'bold', label: 'Bold cartoon', style: 'bold cartoon sticker, thick black outlines, bright flat fills, slight gloss', palette: 'Bright saturated primaries with black outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'minimal', label: 'Minimal line', style: 'minimal single-line sticker, one clean continuous line, no fill', palette: 'Black line only, one accent colour', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['a toadstool ring instead', 'for a plant pot', 'in Halloween colors'],
    directionHint: 'try: a toadstool ring instead',
    seo: {
        title: 'Mushroom Stickers: Fly Agaric, Cluster, Face',
        metaDescription: 'Nine cottagecore mushroom stickers — a fly agaric, a cluster, a smiling mushroom — in retro, bold cartoon, and minimal line styles.',
        h1: 'Mushroom stickers: fly agaric, cluster, smiling mushroom',
        tagline: 'Nine stickers, three cottagecore mushrooms, three styles each.',
        intro: 'The cottagecore mushroom, in three familiar shapes: a red-capped fly agaric with white spots, a mossy cluster of small brown mushrooms, and a mushroom with a small smiling face. Each rendered retro, bold cartoon, and minimal single-line. Ask for a moon-lit forest scene, a fairy-house version, or a different mushroom species, and the same three styles carry it.',
        useCases: [
            'a water bottle or journal cover',
            'a cottagecore planner spread',
            'a nursery or bedroom wall',
            "a nature-lover's gift",
        ],
        faq: [
            {q: "What's in this pack?", a: 'Nine mushroom stickers — a fly agaric, a cluster of small mushrooms, and a mushroom with a face — in retro, bold cartoon, and minimal line finishes.'},
            {q: 'Can I get a forest scene or a different mushroom?', a: 'Yes — the direction field takes any variation, including a moon-lit forest scene, a fairy-house version, or a different mushroom species.'},
            {q: 'What size should I print these at?', a: 'Each sticker is a square die-cut design, so it scales cleanly from a small vinyl sticker to a full A4 sheet.'},
            {q: 'Are these accurate mushroom illustrations for foraging?', a: 'No — these are decorative cottagecore illustrations, not a field guide; do not use them to identify mushrooms for eating.'},
        ],
    },
}
